import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  Divider,
} from "@mui/material";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import { Activity, Wrench } from "@mynaui/icons-react";
import DiagnosticsPopup from "./DiagnosticsPopup";

const AssetCard = ({ asset }) => {
  const navigate = useNavigate();
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const getSeverityColor = (severity) => {
    const level = severity?.toLowerCase();

    switch (level) {
      case "high":
      case "critical":
        return "#C10007";
      case "medium":
        return "#BB4D00";
      case "low":
        return "#008236";
      default:
        return "#6B7280";
    }
  };

  const getSeverityBgColor = (severity) => {
    const level = severity?.toLowerCase();

    switch (level) {
      case "high":
      case "critical":
        return "#FFC9C9";
      case "medium":
        return "#FEE685";
      case "low":
        return "#DCFCE7";
      default:
        return "#F3F4F6";
    }
  };
  return (
    <>
    <Card
      elevation={0}
      sx={{
        height: "100%",
        cursor: "pointer",
        boxShadow: "none",
        "&:hover": { borderColor: "#D1D5DB" },
      }}
      onClick={() => navigate(`/assets/${asset.id}`)}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2.5,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {asset.image ? (
              <Box
                component="img"
                src={asset.image}
                alt={asset.name}
                sx={{
                  width: 64,
                  height: 64,
                  objectFit: "cover",
                  borderRadius: "8px",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: "#F1F5F9",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PrecisionManufacturingIcon
                  sx={{ fontSize: 24, color: "#64748B" }}
                />
              </Box>
            )}
            <Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "24px",
                  color: "#0A0A0A",
                  marginBottom: "5px",
                }}
              >
                {asset.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "16px",
                  color: "#555555",
                }}
              >
                {asset.issue}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={asset.status}
            size="small"
            sx={{
              bgcolor: "#DCFCE7",
              color: "#008236",
              borderRadius: "8px",
              border: "1px solid #008236",
              fontWeight: 600,
              fontSize: "12px",
              lineHeight: "16px",
              padding: "2px 8px",
              height: "auto",
              textTransform: "capitalize",
            }}
          />
        </Box>

        {/* Metrics */}
        <Box sx={{ display: "flex", gap: 2, mb: 2.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "16px",
                color: "#555555",
                mb: 1,
              }}
            >
              Asset Risk
            </Typography>
            <Chip
              label={asset.riskLevel}
              size="small"
              sx={{
                bgcolor: getSeverityBgColor(asset.riskLevel),
                color: getSeverityColor(asset.riskLevel),
                border: `1px solid ${getSeverityColor(asset.riskLevel)}`,
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "12px",
                lineHeight: "16px",
                padding: "2px 8px",
                height: "auto",
                textTransform: "capitalize",
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "16px",
                color: "#555555",
                mb: 1,
              }}
            >
              RUL
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "20px",
                color: asset.rulColor,
              }}
            >
              {asset.rul.value}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "16px",
                color: "#555555",
                mb: 1,
              }}
            >
              Failures - Last month
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "20px",
                color: "#0A0A0A",
              }}
            >
              {asset.failuresLastMonth}
            </Typography>
          </Box>
        </Box>

        <Divider
          sx={{ bgcolor: "#E5E7EB", height: 1, borderRadius: 1, mb: 2 }}
        />

        {/* Last Checked */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "16px",
            color: "#555555",
            mb: 1.3,
          }}
        >
          Last checked: {asset.lastChecked}
        </Typography>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Activity size={20} strokeWidth={1.5} />}
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderColor: "#E2E8F0",
              color: "#0F172B",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "20px",
              padding: "8px 16px",
              borderRadius: "8px",
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
              "&:hover": { borderColor: "#D1D5DB", bgcolor: "#F9FAFB" },
            }}
          >
            Current Health Overview
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Wrench size={20} strokeWidth={1.5} />}
            onClick={(e) => { e.stopPropagation(); setDiagnosticsOpen(true); }}
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderColor: "#E2E8F0",
              color: "#0F172B",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "20px",
              padding: "8px 16px",
              borderRadius: "8px",
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
              "&:hover": { borderColor: "#D1D5DB", bgcolor: "#F9FAFB" },
            }}
          >
            View Diagnostics
          </Button>
        </Box>
      </CardContent>

    </Card>

    <DiagnosticsPopup
      open={diagnosticsOpen}
      onClose={() => setDiagnosticsOpen(false)}
      assetId={asset.id}
      assetName={asset.name}
    />
    </>
  );
};

export default AssetCard;
